// Atomic queue position service using Upstash Redis REST API
// Redis handles ONLY the counter (no personal data).
// All booking data still goes to DynamoDB via AppSync.
//
// How it works:
//   Redis key: "pos:BLR13:2026-03-25:10:00-11:00"
//   Redis INCR returns the next unique position atomically.
//   Even 20+ simultaneous users get unique positions — guaranteed.
//   Keys auto-expire after 24 hours.

const UPSTASH_URL = process.env.REACT_APP_UPSTASH_REDIS_REST_URL || 'https://apt-falcon-81361.upstash.io';
const UPSTASH_TOKEN = process.env.REACT_APP_UPSTASH_REDIS_REST_TOKEN || 'gQAAAAAAAT3RAAIncDI4MWY0ODYzMDMzY2M0NWY3OTg3OGQwNGM4NTQ0ZGVjY3AyODEzNjE';

const MAX_SLOTS = 20;

class DynamoQueueService {
  /**
   * Build the Redis key for a studio/date/slot.
   */
  _redisKey(studioCode, date, slotId) {
    const d = new Date(date);
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return `pos:${studioCode}:${dateKey}:${slotId}`;
  }

  /**
   * Execute a Redis command via Upstash REST API.
   * Upstash REST format: POST with body as array of command parts.
   */
  async _redisCommand(...args) {
    const response = await fetch(`${UPSTASH_URL}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${UPSTASH_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(args)
    });

    if (!response.ok) {
      throw new Error(`Redis error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`Redis error: ${data.error}`);
    }
    return data.result;
  }

  /**
   * Get the next atomic queue position.
   *
   * Uses Redis INCR — a single atomic command that:
   *   1. Increments the counter by 1
   *   2. Returns the new value
   *   3. Creates the key with value 1 if it doesn't exist
   * All in one operation. No race conditions possible.
   */
  async getAtomicPosition(studioCode, date, slotId) {
    const key = this._redisKey(studioCode, date, slotId);

    // Skip Redis if not configured — go straight to DynamoDB fallback
    if (!UPSTASH_URL || !UPSTASH_TOKEN) {
      console.warn('⚠️ Upstash Redis not configured, using DynamoDB fallback');
      return await this._fallbackPosition(studioCode, date, slotId);
    }

    try {
      // Atomic increment — returns the new position
      const position = await this._redisCommand('INCR', key);

      // Set expiry on first creation (24 hours)
      if (position === 1) {
        await this._redisCommand('EXPIRE', key, 86400).catch(() => {});
      }

      // Check if slot is full
      if (position > MAX_SLOTS) {
        // Decrement back since we can't use this position
        await this._redisCommand('DECR', key).catch(() => {});
        throw new Error(`Slot is full (${MAX_SLOTS}/${MAX_SLOTS} bookings)`);
      }

      console.log(`✅ Redis atomic position: ${position} for ${key}`);
      return { position, isAtomic: true };

    } catch (error) {
      if (error.message?.includes('Slot is full')) {
        throw error;
      }

      // Redis unavailable — fall back to DynamoDB counting
      console.warn('⚠️ Redis unavailable, falling back to DynamoDB count:', error.message);
      return await this._fallbackPosition(studioCode, date, slotId);
    }
  }

  /**
   * Rollback a position if the booking save fails.
   * Decrements the Redis counter so the position can be reused.
   */
  async rollbackPosition(studioCode, date, slotId) {
    try {
      const key = this._redisKey(studioCode, date, slotId);
      const current = await this._redisCommand('GET', key);
      if (current && parseInt(current) > 0) {
        await this._redisCommand('DECR', key);
        console.log(`🔄 Position rolled back for ${key}`);
      }
    } catch (error) {
      console.warn('Rollback failed (non-critical):', error.message);
    }
  }

  /**
   * Get current count for a slot (for availability display).
   */
  async getSlotCount(studioCode, date, slotId) {
    try {
      const key = this._redisKey(studioCode, date, slotId);
      const count = await this._redisCommand('GET', key);
      return parseInt(count) || 0;
    } catch (error) {
      console.warn('Slot count check failed:', error.message);
      return 0;
    }
  }

  /**
   * Fallback: count existing bookings in DynamoDB.
   * Used only when Redis is unavailable.
   */
  async _fallbackPosition(studioCode, date, slotId) {
    try {
      const { getPhotoStudioAppointments } = await import('./photoStudioService');
      const result = await getPhotoStudioAppointments();
      if (result.success && result.data) {
        const targetDate = new Date(date).toDateString();
        const existing = result.data.filter(booking => {
          if (booking.formType === 'counter') return false;
          const bookingDate = new Date(booking.submissionDate || booking.createdAt).toDateString();
          return booking.siteCode === studioCode &&
            bookingDate === targetDate &&
            (booking.timeSlot === slotId ||
              (booking.estimatedTime?.includes('|') && booking.estimatedTime.split('|')[1] === slotId)) &&
            booking.status !== 'Cancelled' && booking.status !== 'Expired';
        });

        const positions = existing.map(b => parseInt(b.queuePosition)).filter(p => !isNaN(p) && p > 0);
        const highest = positions.length > 0 ? Math.max(...positions) : 0;
        const position = highest + 1;

        console.log(`📁 DynamoDB fallback position: ${position}`);
        return { position, isAtomic: false };
      }
    } catch (error) {
      console.error('Fallback position failed:', error);
    }
    return { position: 1, isAtomic: false };
  }
}

export default new DynamoQueueService();
